/**
 * Dark hero scanner — animated terminal-style mock that suggests Auditor
 * is actively scanning the URL. Pure CSS animations + animated SVG scan
 * beam over a faint grid + a stream of cycling status lines.
 */
import { useEffect, useState } from "react";
import { CheckCircle, CircleNotch } from "@phosphor-icons/react";

const STAGES = [
  { label: "Fetching markup",         status: "ok" },
  { label: "Parsing DOM tree",        status: "ok" },
  { label: "Running 11 SEO checks",   status: "ok" },
  { label: "Scoring GEO + AEO signals", status: "ok" },
  { label: "Measuring TTFB & payload", status: "ok" },
  { label: "Inspecting SSL & headers", status: "ok" },
  { label: "Auditing accessibility",   status: "active" },
  { label: "Fingerprinting tech stack", status: "pending" },
  { label: "Drafting recommendations", status: "pending" },
];

export default function DarkScannerHero() {
  const [activeIdx, setActiveIdx] = useState(5);

  useEffect(() => {
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % STAGES.length);
    }, 1100);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      className="relative h-full w-full rounded-sm overflow-hidden text-white p-6 lg:p-7 border border-white/10"
      style={{
        background:
          "radial-gradient(ellipse 70% 50% at 0% 100%, rgba(0,82,168,0.20), transparent 60%)," +
          "linear-gradient(180deg, rgba(11,11,13,0.85) 0%, rgba(5,5,7,0.92) 100%)",
        backdropFilter: "blur(2px)",
      }}
      data-testid="dark-scanner"
    >
      {/* Subtle horizontal scanlines for tech texture (no grid) */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-screen"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.9) 0px, rgba(255,255,255,0.9) 1px, transparent 1px, transparent 3px)",
        }}
      />
      {/* Moving scan beam */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand to-transparent scan-beam pointer-events-none" />
      {/* Soft radial glow */}
      <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-brand opacity-25 blur-3xl pointer-events-none" />

      <div className="relative">
        {/* URL input mock */}
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />
          <span className="text-[10px] mono tracking-wider text-white/60">LIVE AUDIT</span>
        </div>
        <div className="bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 mb-5 mono text-sm flex items-center gap-2">
          <span className="text-white/40">https://</span>
          <span className="text-white">fasterbetter.ca</span>
          <span className="ml-auto inline-block w-1.5 h-4 bg-brand animate-pulse" />
        </div>

        {/* Stage list */}
        <div className="space-y-1.5 mb-5">
          {STAGES.map((s, i) => {
            const isActive = i === activeIdx;
            const isDone = i < activeIdx;
            return (
              <div
                key={s.label}
                className={`flex items-center gap-2 mono text-xs transition-colors ${
                  isDone ? "text-success" : isActive ? "text-white" : "text-white/30"
                }`}
              >
                {isDone ? (
                  <CheckCircle size={12} weight="fill" className="shrink-0" />
                ) : isActive ? (
                  <CircleNotch size={12} weight="bold" className="shrink-0 animate-spin" />
                ) : (
                  <span className="w-3 h-3 rounded-full border border-white/30 shrink-0" />
                )}
                <span>{s.label}</span>
                <span className="ml-auto text-[10px] text-white/40">
                  {isDone ? "OK" : isActive ? "···" : ""}
                </span>
              </div>
            );
          })}
        </div>

        {/* Mock score readout */}
        <div className="grid grid-cols-6 gap-1.5 mb-4">
          {[
            ["SEO",  73, "text-success"],
            ["GEO",  38, "text-critical"],
            ["AEO",  44, "text-critical"],
            ["PERF", 62, "text-warning"],
            ["SEC",  55, "text-warning"],
            ["A11Y", 57, "text-warning"],
          ].map(([k, v, cls]) => (
            <div key={k} className="border border-white/15 rounded-sm py-2 px-1 text-center bg-white/5">
              <div className="text-[8px] mono tracking-widest text-white/40">{k}</div>
              <div className={`num-display text-lg font-black ${cls}`}>{v}</div>
            </div>
          ))}
        </div>

        <div className="text-[10px] mono text-white/40 leading-relaxed">
          <span className="text-success">▸</span> fetched · 2.21s · 12 issues found · drafting fix-list…
        </div>
      </div>

      <style>{`
        @keyframes scan-beam {
          0%   { transform: translateY(0);    opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(640px); opacity: 0; }
        }
        .scan-beam {
          animation: scan-beam 3.4s linear infinite;
        }
      `}</style>
    </div>
  );
}
